import { memo, useCallback, useState } from 'react';
import classNames from 'classnames';
import { linkWithHttps } from '@/utils/helpers';
import { useCloseOnEscape } from '../../utils/useCloseOnEscape';
import { ApplyModal } from './ApplyModal';
import styles from './JobPostings.module.scss';

const LOCATION_TYPE_LABELS = {
  in_person: 'In Person',
  fully_remote: 'Fully Remote',
  hybrid: 'Hybrid',
  on_the_road: 'On the Road'
};

const formatJobLocation = job => {
  if (job.city && job.state) return ` · ${job.city}, ${job.state}`;
  if (job.city) return ` · ${job.city}`;
  if (job.state) return ` · ${job.state}`;
  if (job.location_type === 'fully_remote') return ' · Remote';
  if (job.location_type === 'on_the_road') return ' · Nationwide';
  return '';
};

const JobBadges = memo(({ job }) => (
  <div className={styles.jobBadges}>
    <span className={styles.badge}>{job.employment_type}</span>
    <span className={classNames(styles.badge, styles.badgeLocation)}>
      {LOCATION_TYPE_LABELS[job.location_type] || job.location_type}
    </span>
  </div>
));

const JobDetailList = memo(({ title, items }) => {
  if (!items?.length) return null;

  return (
    <div className={styles.jobDetailSection}>
      <h4 className={styles.jobDetailSectionTitle}>{title}</h4>
      <ul className={styles.jobDetailList}>
        {items.map((item, index) => (
          <li key={index}>{item}</li>
        ))}
      </ul>
    </div>
  );
});

const JobDetailModal = memo(({ job, companyName, onClose, onApply }) => {
  useCloseOnEscape({ isOpen: true, onCloseModal: onClose });

  if (!job) return null;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modal} onClick={event => event.stopPropagation()}>
        <button type="button" className={styles.modalClose} onClick={onClose} aria-label="Close job details">
          ×
        </button>

        <div className={styles.jobDetailHeader}>
          <p className={styles.jobDetailLabel}>Now Hiring</p>
          <h3 className={styles.jobDetailTitle}>{job.title}</h3>
          <p className={styles.jobDetailCompany}>
            {job.company_name || companyName}
            {formatJobLocation(job)}
          </p>
          <JobBadges job={job} />
        </div>

        <div className={styles.jobDetailBody}>
          {job.short_description && <p className={styles.jobIntro}>{job.short_description}</p>}
          {job.description && (
            <div className={styles.jobDetailSection}>
              <h4 className={styles.jobDetailSectionTitle}>Description</h4>
              <p>{job.description}</p>
            </div>
          )}

          <div className={styles.jobDetailRow}>
            {job.time_commitment && (
              <div className={styles.jobDetailCol}>
                <h4 className={styles.jobDetailSectionTitle}>Time Commitment</h4>
                <p>{job.time_commitment}</p>
              </div>
            )}
            {job.work_schedule && (
              <div className={styles.jobDetailCol}>
                <h4 className={styles.jobDetailSectionTitle}>Work Schedule</h4>
                <p>{job.work_schedule}</p>
              </div>
            )}
          </div>

          <JobDetailList title="Requirements" items={job.requirements} />
          <JobDetailList title="Responsibilities" items={job.responsibilities} />
          <JobDetailList title="Benefits & Compensation" items={job.benefits_and_compensation} />
        </div>

        <div className={styles.jobDetailActions}>
          <button type="button" className={styles.applyButton} onClick={() => onApply(job)}>
            Apply Now
          </button>
        </div>
      </div>
    </div>
  );
});

export const JobPostings = memo(({ jobPostings = [], companyName = '', webpageUsername = '' }) => {
  const [selectedJob, setSelectedJob] = useState(null);
  const [applyJob, setApplyJob] = useState(null);

  const handleApply = useCallback(job => {
    if (job.indeed_url) {
      window.open(linkWithHttps(job.indeed_url), '_blank');
      return;
    }

    setSelectedJob(null);
    setApplyJob(job);
  }, []);

  if (!jobPostings.length) return null;

  return (
    <div className={styles.jobPostingsContainer}>
      <h3 className={styles.jobPostingsHeading}>Browse Our Career Opportunities</h3>

      <ul className={styles.jobPostingsList}>
        {jobPostings.map(job => (
          <li key={job.id} className={styles.jobCard}>
            <button type="button" className={styles.jobCardButton} onClick={() => setSelectedJob(job)}>
              <div className={styles.jobCardContent}>
                <h4 className={styles.jobTitle}>{job.title}</h4>
                <p className={styles.jobCompany}>
                  {job.company_name || companyName}
                  {formatJobLocation(job)}
                </p>
                <JobBadges job={job} />
                {job.short_description && <p className={styles.jobShortDesc}>{job.short_description}</p>}
              </div>
            </button>

            <button type="button" className={styles.applyButton} onClick={() => handleApply(job)}>
              Apply Now
            </button>
          </li>
        ))}
      </ul>

      {selectedJob && (
        <JobDetailModal
          job={selectedJob}
          companyName={companyName}
          onClose={() => setSelectedJob(null)}
          onApply={handleApply}
        />
      )}

      {applyJob && (
        <ApplyModal
          job={applyJob}
          companyName={companyName}
          webpageUsername={webpageUsername}
          onClose={() => setApplyJob(null)}
        />
      )}
    </div>
  );
});
